import { Component, OnInit, OnDestroy, Output, EventEmitter, Input } from '@angular/core';
import { ReplaySubject } from 'rxjs';

import { NONE_PLAYLIST } from 'app/modules/social/services/playlist.service';
import { VideoChatService } from 'app/modules/social/services/videochat.service';
import { Timeout } from 'app/types';
import Playlist from 'entity/Playlist';
import Paging from 'rest/Paging';

const PREVIEW_TRACK_URL = '/api/downloads/audio/preview/';

@Component({
  selector: 'music-card',
  templateUrl: './music-card.component.html',
  styleUrls: ['./music-card.component.scss'],
  standalone: false,
})
export class MusicCardComponent implements OnInit, OnDestroy {
  private destroyed$ = new ReplaySubject<boolean>(1);
  private audio = new Audio();
  protected playingId?: string;
  public showLoader = false;

  @Input() public hasBackStepButton = true;
  @Input() public selectedPlaylistId?: string;
  @Input() public isCreateFlow = true;
  @Output() public selectedPlaylistIdChange = new EventEmitter<string>();
  @Output() public backStepClick = new EventEmitter<boolean>();

  public playlistOptions?: Playlist[];
  public playlistSelectedId?: string;
  private intervalPreviewId!: Timeout;

  public constructor(private videoChatService: VideoChatService) {}

  public async ngOnInit(): Promise<void> {
    this.audio.id = 'music-card';
    this.showLoader = true;
    await this.getPlaylists();
    if (this.selectedPlaylistId) {
      this.playlistSelectedId = this.selectedPlaylistId;
    }
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  public async getPlaylists(): Promise<void> {
    const paging = new Paging(0, 100);
    const playlists = (await this.videoChatService.getAllPlaylists(paging)) ?? [];

    playlists.sort((a, b) => {
      if (a.name === undefined) {
        return 1;
      }
      if (b.name === undefined) {
        return -1;
      }

      return a.name.localeCompare(b.name);
    });

    this.playlistOptions = [NONE_PLAYLIST, ...playlists];
    this.showLoader = false;
  }

  public selectPlaylist(playlistId?: string): void {
    this.playlistSelectedId = playlistId;
  }

  public nextStep(): void {
    this.selectedPlaylistIdChange.emit(this.playlistSelectedId);
  }

  public backStep(): void {
    this.backStepClick.emit(true);
  }

  public async playExampleTrack(playlistId?: string): Promise<void> {
    if (this.audio.paused || this.playingId !== playlistId) {
      const exampleTrack = await this.videoChatService.getFirstTrackFromPlaylist(playlistId?.toString());
      // Clear interval if user selects a different playlist without finishing or stop the previous selection
      clearInterval(this.intervalPreviewId);
      this.playingId = playlistId;
      this.audio.src = `${PREVIEW_TRACK_URL}${exampleTrack?.audioTrackId}`;
      await this.audio.play();

      if (!this.audio.paused) {
        this.intervalPreviewId = setInterval(() => {
          this.playlistSelectedId = '';
          this.playingId = '';
          this.audio.pause();
        }, 15000);
      }
    } else {
      this.audio.pause();
      this.playingId = '';
      clearInterval(this.intervalPreviewId);
    }
  }

  public playlistNone(playlistId?: string): boolean {
    return playlistId === NONE_PLAYLIST.playlistId;
  }

  public isPlaying(playlistId?: string): boolean {
    return playlistId === this.playingId;
  }

  public disableApplyButton(): boolean {
    return !this.isCreateFlow && this.selectedPlaylistId === this.playlistSelectedId;
  }
}
